import { FC, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import { DialogContent, Stack } from "@mui/material";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import * as yup from "yup";
import { useEffectOnceWhen } from "rooks";
import { htmlToSlackMd } from "./utils/htmlToSlackMd";
import { SlackConversationsSelect } from "./SlackConversationsSelect";
import { CreateIntegrationMessageEditor } from "../common/CreateIntegrationMessageEditor";
import { CreateIntegrationResourcesTable } from "../common/CreateIntegrationResourcesTable";
import {
  PostUsersMeSlackIntegrationsByIntegrationIdMessageApiArg,
  ResourceType,
  SlackConversation,
  usePostUsersMeSlackIntegrationsByIntegrationIdMessageMutation,
} from "../../../../../services/cloudchipr.api";
import { DialogTitleClosable } from "../../../dialog-utils/DialogTitleClosable";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { currentAccountIdSelector } from "../../../../../store/account/selectors/current-account/currentAccountIdSelector";
import { slackMessageSelector } from "../../../../../store/integrations/selectors/slack/slackMessageSelector";
import { getIntegrationByIdThunk } from "../../../../../store/integrations/thunks/getIntegrationByIdThunk";
import { CreateIntegrationMessageFormActions } from "../common/CreateIntegrationMessageFormActions";
import { IntegrationSelect } from "../common/IntegrationSelect";
import { currentAccountSelectedResourcesByResourceTypeCsvDataSelector } from "../../../../../store/account/selectors/current-account/resources/selection/currentAccountSelectedResourcesByResourceTypeCsvDataSelector";

interface CreateSlackMessageDialogProps {
  resourceType: ResourceType;
  onClose(): void;
}

export const validationSchema = yup.object({
  integrationId: yup.string().required("This field is required"),
  body: yup.object({
    conversations: yup
      .array()
      .min(1, "At least one Slack conversation should be selected."),
  }),
});

const initialValues: PostUsersMeSlackIntegrationsByIntegrationIdMessageApiArg =
  {
    integrationId: "",
    body: {
      account_id: "",
      message: "",
      conversations: [],
      resources: [],
    },
  };

export const CreateSlackMessageDialog: FC<CreateSlackMessageDialogProps> = ({
  resourceType,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const accountId = useAppSelector(currentAccountIdSelector);
  const message = useAppSelector((state) =>
    slackMessageSelector(state, resourceType),
  );

  const resources = useAppSelector((state) =>
    currentAccountSelectedResourcesByResourceTypeCsvDataSelector(
      state,
      resourceType,
    ),
  );

  const [createMessage, { isLoading }] =
    usePostUsersMeSlackIntegrationsByIntegrationIdMessageMutation();

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const payload = {
        ...values,
        body: {
          ...values.body,
          message: htmlToSlackMd(values.body.message ?? ""),
        },
      };

      try {
        await createMessage(payload).unwrap();

        enqueueSnackbar("Slack message successfully sent.", {
          variant: "snackbarAlert",
          AlertSnackBarProps: { severity: "success" },
        });

        onClose();
      } catch (e: any) {
        enqueueSnackbar(e?.data?.message || "Something went wrong", {
          variant: "snackbarAlert",
          AlertSnackBarProps: { severity: "error" },
        });
      }
    },
  });

  const { isValid, values, submitForm, setFieldValue, errors, touched } =
    formik;

  const conversationsChangeHandler = useCallback(
    (conversations: SlackConversation[]) => {
      setFieldValue("body.conversations", conversations);
    },
    [setFieldValue],
  );

  const integrationChangeHandler = useCallback(
    (integrationId: string) => {
      setFieldValue("integrationId", integrationId);

      dispatch(
        getIntegrationByIdThunk({ id: integrationId, type: "slack" }),
      ).then((response: any) => {
        const conversation = response?.payload?.data?.conversation;
        setFieldValue("body.conversations", conversation ? [conversation] : []);
      });
    },
    [dispatch, setFieldValue],
  );

  const messageChangeHandler = useCallback(
    (value: string) => setFieldValue("body.message", value),
    [setFieldValue],
  );

  useEffectOnceWhen(
    () => {
      setFieldValue("body.account_id", accountId);
      setFieldValue("body.message", message);
      setFieldValue("body.resources", resources);
    },
    !!accountId && !!message && !!resources?.length,
  );

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitleClosable title="Send Slack" onClose={onClose} />

      <DialogContent dividers>
        <IntegrationSelect
          type="slack"
          value={values.integrationId}
          onChange={integrationChangeHandler}
          error={touched.integrationId ? errors.integrationId : ""}
        />

        <SlackConversationsSelect
          disabled={!values.integrationId}
          integrationId={values.integrationId}
          onChange={conversationsChangeHandler}
          selectedConversations={values.body.conversations}
          error={touched.body?.conversations ? errors.body?.conversations : ""}
        />

        <Stack p={2} my={3} spacing={2} bgcolor="grey.50">
          <CreateIntegrationResourcesTable
            label="Resources.csv (snippet)"
            resourceType={resourceType}
          />

          <CreateIntegrationMessageEditor
            value={values.body.message ?? ""}
            onChange={messageChangeHandler}
          />
        </Stack>
      </DialogContent>

      <CreateIntegrationMessageFormActions
        disabled={!isValid}
        onCancel={onClose}
        onSubmit={submitForm}
        isLoading={isLoading}
      />
    </Dialog>
  );
};
